import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getRecipe } from '../data/recipes';
import { stepColor } from '../lib/stepColors';
import { StepBadge } from '../components/StepBadge';
import { GlossaryText } from '../components/GlossaryText';
import { useWakeLock } from '../lib/wakeLock';
import { useRecipeScale } from '../lib/servings';
import { useRecipeProgress } from '../lib/progress';
import { scaleAmount, yieldUnit } from '../lib/scale';

export default function CookMode() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const recipe = slug ? getRecipe(slug) : undefined;
  const [index, setIndex] = useState(0);
  const scale = useRecipeScale(recipe);
  const progress = useRecipeProgress(recipe);

  useWakeLock(true);

  // Moving past a step marks it done, so the recipe page's ledger and step
  // list match what happened at the stove.
  function advance() {
    if (!recipe) return;
    progress.markStepDone(recipe.steps[index].n);
    setIndex((i) => Math.min(i + 1, recipe.steps.length - 1));
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') advance();
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
      if (e.key === 'Escape') navigate(`/recipe/${slug}`);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipe, slug, navigate, index]);

  if (!recipe) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6 text-center">
        <p className="font-serif text-xl">
          That recipe isn’t in the book —{' '}
          <Link to="/" className="underline decoration-dotted underline-offset-4">
            back to the catalog
          </Link>
          .
        </p>
      </div>
    );
  }

  const step = recipe.steps[index];
  const c = stepColor(step.n);
  const last = index === recipe.steps.length - 1;
  const used = step.ingredientIds
    .map((id) => {
      const ing = recipe.ingredients.find((i) => i.id === id);
      if (!ing) return null;
      const use = ing.uses.find((u) => u.step === step.n) ?? ing.uses[0];
      return { ing, use };
    })
    .filter(Boolean) as {
    ing: (typeof recipe.ingredients)[number];
    use: (typeof recipe.ingredients)[number]['uses'][number];
  }[];

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Top: exit + progress */}
      <div className="border-b border-line/70 px-3 pb-2 pt-3">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <Link
            to={`/recipe/${recipe.slug}`}
            aria-label="Leave cook mode"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line bg-card text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </Link>
          <div className="min-w-0 flex-1">
            <p className="truncate font-serif text-[0.95rem] font-medium">{recipe.title}</p>
            <p className="font-sans text-[0.75rem] text-ink-soft" aria-live="polite">
              Step {step.n} of {recipe.steps.length}
              {scale.isScaled && (
                <span className="font-semibold text-goal">
                  {' '}· scaled to {scale.current} {yieldUnit(recipe.yield)}
                </span>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-1" aria-hidden="true">
            {recipe.steps.map((s, i) => (
              <span
                key={s.n}
                className="h-1.5 w-4 rounded-full"
                style={{ backgroundColor: i <= index ? stepColor(s.n).base : 'var(--color-line)' }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Ingredients for this step, pinned */}
      {used.length > 0 && (
        <div className="border-b border-line/70 px-3 py-2.5" style={{ backgroundColor: c.tint }}>
          <ul
            className="mx-auto flex max-w-2xl flex-wrap gap-x-4 gap-y-1"
            aria-label={`Ingredients for step ${step.n}`}
          >
            {used.map(({ ing, use }) => (
              <li key={ing.id} className="font-sans text-[1rem]" style={{ color: c.deep }}>
                <span className="font-bold">{scaleAmount(use.amount, scale.factor).text}</span> {ing.name.toLowerCase()}
                {use.note && <span className="italic"> — {use.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The step */}
      <main className="mx-auto w-full max-w-2xl flex-1 px-5 py-6">
        <div className="flex items-center gap-3.5">
          <StepBadge n={step.n} size="lg" />
          <h1 className="font-serif text-3xl font-semibold leading-tight sm:text-4xl">{step.title}</h1>
        </div>
        {step.time && <p className="mt-1.5 font-sans text-[0.9rem] font-medium text-ink-soft">{step.time}</p>}
        {step.critical && (
          <p
            className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-sans text-[0.8rem] font-bold uppercase tracking-[0.08em]"
            style={{ backgroundColor: c.tint, color: c.deep }}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-7h-2v5h2V9z" />
            </svg>
            Don’t skip this
          </p>
        )}
        <GlossaryText
          body={step.body}
          glossary={recipe.glossary}
          ingredients={recipe.ingredients}
          factor={scale.factor}
          linkIngredients={false}
          className="mt-5 text-[1.35rem] leading-[1.65] sm:text-[1.5rem]"
        />
        {step.tip && (
          <aside
            className="mt-6 rounded-xl border-l-4 bg-card p-4 font-serif text-[1.05rem] italic leading-relaxed text-ink-soft"
            style={{ borderColor: c.base }}
          >
            <span className="font-sans text-[0.72rem] font-semibold uppercase not-italic tracking-[0.08em]">Tip </span>
            <GlossaryText
              body={step.tip}
              glossary={[]}
              ingredients={recipe.ingredients}
              factor={scale.factor}
              linkIngredients={false}
              className="inline [&>p]:inline [&>p]:font-serif [&>p]:italic"
            />
          </aside>
        )}
      </main>

      {/* Thumb bar */}
      <div className="sticky bottom-0 border-t border-line bg-paper/95 px-3 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-2xl gap-2.5">
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(i - 1, 0))}
            disabled={index === 0}
            className="h-16 flex-1 rounded-2xl border border-line bg-card font-sans text-lg font-semibold text-ink transition-colors hover:border-ink-soft disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            Back
          </button>
          {last ? (
            <Link
              to={`/recipe/${recipe.slug}`}
              onClick={() => progress.markStepDone(step.n)}
              className="flex h-16 flex-[2] items-center justify-center rounded-2xl bg-ink font-sans text-lg font-semibold text-paper transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            >
              Done — back to the recipe
            </Link>
          ) : (
            <button
              type="button"
              onClick={advance}
              className="h-16 flex-[2] rounded-2xl bg-ink font-sans text-lg font-semibold text-paper transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            >
              Next step
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
